import { useCallback, useContext, useEffect } from 'react'

import { isRewardFareClass, fareClassToUrlMap, type FareClassWithReward } from 'constants/flight'
import { useCharterFilterUrlState } from 'hooks/CharterFlights/useCharterFilterUrlState'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useCharterFareClassPolicy from 'hooks/Flights/useCharterFareClassPolicy'
import useMaldivesCharterModalFareClasses from 'hooks/Flights/useMaldivesCharterModalFareClasses'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import MaldivesCharterFlightFareClassModal from 'components/OfferPage/Filters/CharterFlight/MaldivesCharterFlightFareClassModal'

interface Params {
  offer?: App.Offer
  fareClass: FareClassWithReward
  checkInDate?: string
  checkOutDate?: string
  occupancies: Array<App.Occupants>
  feederOriginAirportCode?: string
}

interface OpenOptions {
  /** Override the modal's initial rewards-only toggle state. Use when the caller has just
   *  flipped the URL via `setRewardSeatsOnly` and the parent hasn't re-rendered yet, so the
   *  hook's captured value is stale. */
  rewardSeatsOnly?: boolean
}

interface Result {
  canOpen: boolean
  open: (options?: OpenOptions) => Promise<void>
  hasExplicitFareClass: boolean
}

export default function useOpenCharterFareClassModal({
  offer,
  fareClass,
  checkInDate,
  checkOutDate,
  occupancies,
  feederOriginAirportCode,
}: Params): Result {
  const openModal = useContext(ModalContext)
  const dispatch = useOfferPageDispatch()
  const { fareClassUrlRaw, fareClassFromUrl, setFareClassUrl, setCabinFilter, rewardSeatsOnly, setRewardSeatsOnly } = useCharterFilterUrlState()

  useEffect(() => {
    if (fareClassFromUrl && fareClassFromUrl !== fareClass) {
      dispatch({ type: OfferPageActions.setFareClass, fareClass: fareClassFromUrl })
    }
  }, [fareClassFromUrl, fareClass, dispatch])

  const charterFareClassPolicy = useCharterFareClassPolicy(offer, occupancies)
  const allowedFareClassesForModal = useMaldivesCharterModalFareClasses(charterFareClassPolicy.allowedFareClasses)
  const canOpen = allowedFareClassesForModal.length > 1

  const open = useCallback(async(options?: OpenOptions) => {
    const effectiveRewardSeatsOnly = options?.rewardSeatsOnly ?? rewardSeatsOnly ?? false
    const selectedClass = await openModal<FareClassWithReward>(
      <MaldivesCharterFlightFareClassModal
        selectedClassId={fareClass}
        checkInDate={checkInDate}
        checkOutDate={checkOutDate}
        occupancies={occupancies}
        allowedFareClasses={allowedFareClassesForModal}
        feederOriginAirportCode={feederOriginAirportCode}
        rewardSeatsOnly={effectiveRewardSeatsOnly}
        onRewardSeatsOnlyChange={setRewardSeatsOnly}
      />,
    )
    if (selectedClass) {
      dispatch({ type: OfferPageActions.setFareClass, fareClass: selectedClass })
      setFareClassUrl(fareClassToUrlMap[selectedClass])
      setCabinFilter(selectedClass)
      if (isRewardFareClass(selectedClass)) {
        setRewardSeatsOnly(true)
      }
    }
  }, [openModal, fareClass, checkInDate, checkOutDate, occupancies, allowedFareClassesForModal, feederOriginAirportCode, dispatch, setFareClassUrl, setCabinFilter, setRewardSeatsOnly, rewardSeatsOnly])

  return { canOpen, open, hasExplicitFareClass: !!fareClassUrlRaw }
}
